/**
 * ShopifyContext - Provides Shopify embedded app context with session token auth
 * 
 * This context handles:
 * - Embedded mode detection
 * - Session token fetching via App Bridge
 * - Authenticated API calls with Bearer token
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import createApp from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge-utils';

const ShopifyContext = createContext(null);

// Shopify API Key (must match backend)
const SHOPIFY_API_KEY = 'cdcc4892b40e6abb8f76ec84d4d98c1f';

/**
 * Parse URL parameters to detect embedded mode
 */
function getShopifyParams() {
  const params = new URLSearchParams(window.location.search);
  const host = params.get('host');
  const shop = params.get('shop');
  const embedded = params.get('embedded') === '1' || params.get('embedded') === 'true';
  
  // Also check if we're in an iframe (backup detection)
  let inIframe = false;
  try {
    inIframe = window.self !== window.top;
  } catch (e) {
    inIframe = true; // Cross-origin iframe
  }
  
  return {
    host,
    shop,
    embedded: embedded || (host && inIframe),
    isEmbedded: !!(host && (embedded || inIframe)),
  };
}

/**
 * Store embedded params in sessionStorage for persistence across navigation
 */
function persistEmbeddedParams(params) {
  if (params.isEmbedded) {
    sessionStorage.setItem('shopify_embedded', 'true');
    if (params.host) sessionStorage.setItem('shopify_host', params.host);
    if (params.shop) sessionStorage.setItem('shopify_shop', params.shop);
  }
}

/**
 * Retrieve embedded params from sessionStorage
 */
function getPersistedParams() {
  return {
    isEmbedded: sessionStorage.getItem('shopify_embedded') === 'true',
    host: sessionStorage.getItem('shopify_host'),
    shop: sessionStorage.getItem('shopify_shop'),
  };
}

export function ShopifyProvider({ children }) {
  const [isReady, setIsReady] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const appRef = useRef(null);
  
  // Get params from URL or sessionStorage
  const urlParams = getShopifyParams();
  const persistedParams = getPersistedParams();
  
  // Prefer URL params, fall back to persisted
  const isEmbedded = urlParams.isEmbedded || persistedParams.isEmbedded;
  const host = urlParams.host || persistedParams.host;
  const shop = urlParams.shop || persistedParams.shop;
  
  // Persist params on mount
  useEffect(() => {
    if (urlParams.isEmbedded) {
      persistEmbeddedParams(urlParams);
    }
  }, [urlParams.isEmbedded, urlParams.host, urlParams.shop]);
  
  // Initialize App Bridge and fetch session token
  useEffect(() => {
    const initAppBridge = async () => {
      if (!isEmbedded || !host) {
        console.log('Not in embedded mode, skipping App Bridge init');
        setIsReady(true);
        return;
      }
      
      try {
        console.log('🔧 Initializing Shopify App Bridge...');
        console.log(`  - host: ${host}`);
        console.log(`  - shop: ${shop}`);
        console.log(`  - apiKey: ${SHOPIFY_API_KEY}`);
        
        // Create App Bridge instance
        const app = createApp({
          apiKey: SHOPIFY_API_KEY,
          host: host,
          forceRedirect: true,
        });
        
        appRef.current = app;
        console.log('✅ App Bridge instance created');
        
        // Fetch initial session token
        await fetchSessionToken(app);
        
        setIsReady(true);
      } catch (error) {
        console.error('❌ Failed to initialize App Bridge:', error);
        setTokenError(error.message);
        setIsReady(true);
      }
    };
    
    initAppBridge();
  }, [isEmbedded, host, shop]);
  
  // Fetch session token from App Bridge
  const fetchSessionToken = useCallback(async (app = appRef.current) => {
    if (!app) {
      console.warn('No App Bridge instance available');
      return null;
    }
    
    try {
      console.log('🔑 Fetching Shopify session token...');
      const token = await getSessionToken(app);
      console.log(`✅ Session token received: ${token.substring(0, 30)}...`);
      setSessionToken(token);
      setTokenError(null);
      return token;
    } catch (error) {
      console.error('❌ Failed to fetch session token:', error);
      setTokenError(error.message);
      return null;
    }
  }, []);
  
  // Refresh token (tokens expire after ~1 minute)
  const refreshToken = useCallback(async () => {
    return await fetchSessionToken();
  }, [fetchSessionToken]);
  
  // Create authenticated fetch function
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    // Get fresh token for each request
    let token = sessionToken;
    
    // Token might be expired, try to refresh
    if (!token || tokenExpired(token)) {
      token = await refreshToken();
    }
    
    if (!token) {
      throw new Error('No session token available. Please reload the app.');
    }
    
    // Add Authorization header
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    
    return fetch(url, {
      ...options,
      headers,
    });
  }, [sessionToken, refreshToken]);
  
  const value = useMemo(() => ({
    isEmbedded,
    isReady,
    host,
    shop,
    sessionToken,
    tokenError,
    app: appRef.current,
    refreshToken,
    authenticatedFetch,
    // Helper to build URLs with embedded params
    buildEmbeddedUrl: (path) => {
      if (!isEmbedded) return path;
      const url = new URL(path, window.location.origin);
      url.searchParams.set('embedded', '1');
      if (host) url.searchParams.set('host', host);
      if (shop) url.searchParams.set('shop', shop);
      return url.pathname + url.search;
    },
    // Clear embedded state (for logout)
    clearEmbeddedState: () => {
      sessionStorage.removeItem('shopify_embedded');
      sessionStorage.removeItem('shopify_host');
      sessionStorage.removeItem('shopify_shop');
      setSessionToken(null);
    },
  }), [isEmbedded, isReady, host, shop, sessionToken, tokenError, refreshToken, authenticatedFetch]);
  
  return (
    <ShopifyContext.Provider value={value}>
      {children}
    </ShopifyContext.Provider>
  );
}

/**
 * Check if JWT token is expired (with 30s buffer)
 */
function tokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    return Date.now() > exp - 30000; // 30 second buffer
  } catch {
    return true; // Assume expired if can't parse
  }
}

export function useShopify() {
  const context = useContext(ShopifyContext);
  if (!context) {
    throw new Error('useShopify must be used within a ShopifyProvider');
  }
  return context;
}

export default ShopifyContext;
